import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { User } from '../../../types/manageUsers';
import { setMultipleUserEmails, toggleUserSelection } from '../../../redux/slices/manageUsers';
import EmailInputRow from './EmailInputRow';
import CsvUploadSection from './CsvUploadSection';
import PersonAvatar from './PersonAvatar';
import useIntegrationStatus from '../../../hooks/useIntegrationStatus';
import { HiChevronLeft, HiPencil, HiX } from 'react-icons/hi';
import { isValidEmail } from '../../../utils/csvUtils';

interface EmailCollectionStepProps {
  selectedUsers: User[];
  onBack: () => void;
  onProceed: () => void;
}

const EmailCollectionStep: React.FC<EmailCollectionStepProps> = ({
  selectedUsers,
  onBack,
  onProceed,
}) => {
  const dispatch = useDispatch();
  const { isIntegrationActive } = useIntegrationStatus();
  
  const {
    userEmails: reduxUserEmails,
  } = useSelector((state: any) => state.manageUsers);

  const [localUserEmails, setLocalUserEmails] = useState<Record<string, string>>(() => {
    return { ...reduxUserEmails };
  });

  const [editingUsers, setEditingUsers] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setLocalUserEmails(prev => ({ ...prev, ...reduxUserEmails }));
  }, [reduxUserEmails]);

  const usersNeedingEmails: User[] = useMemo(() => {
    return selectedUsers.filter((user: User) => {
      const hasOriginalEmail = !!(user.email && user.email.trim() !== '');
      const collected = (localUserEmails[user.id] ?? '').trim();
      const hasValidCollectedEmail = isValidEmail(collected);
      return !hasOriginalEmail && !hasValidCollectedEmail;
    });
  }, [selectedUsers, localUserEmails]);

  const handleEmailChange = (userId: string, email: string) => {
    setLocalUserEmails(prev => ({
      ...prev,
      [userId]: email
    }));
    setEditingUsers(prev => {
      if (!prev[userId]) return prev; 
      const next = { ...prev };
      const valid = isValidEmail(email);
      next[userId] = !valid; 
      return next;
    });
  };

  const handleEmailsUploaded = (emails: Record<string, string>) => {
    setLocalUserEmails(prev => ({
      ...prev,
      ...emails
    }));
  };

  const handleProceedToImport = () => {
    dispatch(setMultipleUserEmails(localUserEmails));
    onProceed();
  };

  const handleRemoveUser = (userId: string) => {
    dispatch(toggleUserSelection(userId));
  };

  const handleBack = () => {
    dispatch(setMultipleUserEmails(localUserEmails));
    onBack();
  };

  const isJira = isIntegrationActive('jira');

  const canProceed = useMemo(() => {
    return selectedUsers.every((user: User) => {
      const hasOriginalEmail = !!(user.email && user.email.trim() !== '');
      const collected = (localUserEmails[user.id] ?? '').trim();
      const collectedValid = isValidEmail(collected);
      return hasOriginalEmail || collectedValid;
    });
  }, [selectedUsers, localUserEmails]);

  const [nextMissingIndex, setNextMissingIndex] = useState(0);

  useEffect(() => {
    setNextMissingIndex(0);
  }, [usersNeedingEmails.length]);

  const scrollToNextMissingEmailUser = () => {
    if (usersNeedingEmails.length === 0) return;
    const targetUser = usersNeedingEmails[nextMissingIndex % usersNeedingEmails.length];
    const element = document.getElementById(`user-${targetUser.id}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setNextMissingIndex((idx) => (idx + 1) % usersNeedingEmails.length);
  };

  return (
    <div className="h-full flex flex-col flex-1 min-h-0 overflow-hidden">
      {/* Content */}
      <div className="flex-1 overflow-hidden px-6 py-4 w-full">
        <div className="h-full w-full flex flex-col space-y-6">
          {selectedUsers.length > 0 && (
            <div className="w-full">
              <CsvUploadSection
                users={selectedUsers}
                editableUserIds={selectedUsers.filter(u => !(u.email && u.email.trim() !== '')).map(u => u.id)}
                isJira={isJira}
                overrides={localUserEmails}
                onEmailsUploaded={handleEmailsUploaded}
              />
            </div>
          )}

          {/* Users List */}
          <div className="w-full flex-1 min-h-0 flex flex-col space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                Users to Import ({selectedUsers.length})
              </h4>
              {usersNeedingEmails.length > 0 && (
                <button
                  type="button"
                  onClick={scrollToNextMissingEmailUser}
                  className="text-xs font-medium text-amber-700 dark:text-amber-300 hover:underline hover:text-amber-800 dark:hover:text-amber-200"
                  title="Jump to next user needing an email"
                >
                   User{usersNeedingEmails.length > 1 ? 's' : ''} email missing ({usersNeedingEmails.length})
                </button>
              )}
            </div>
            
            <div className="w-full flex-1 min-h-0 overflow-y-auto space-y-3">
              {selectedUsers.map((user: User) => {
                const hasOriginalEmail = !!(user.email && user.email.trim() !== '');
                const collected = (localUserEmails[user.id] ?? '').trim();
                const forcedEdit = !!editingUsers[user.id];
                const needsEmail = forcedEdit || (!hasOriginalEmail && !isValidEmail(collected));

                return (
                  <div key={user.id} id={`user-${user.id}`} className="w-full" tabIndex={-1}>
                    {needsEmail ? (
                      <EmailInputRow
                        user={user}
                        email={localUserEmails[user.id] || ''}
                        onEmailChange={handleEmailChange}
                        onRemoveUser={handleRemoveUser}
                      />
                    ) : (
                      <div className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800">
                        <div className="flex items-center justify-between w-full">
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <div className="w-8 h-8 flex-shrink-0">
                              <PersonAvatar
                                className="!rounded-md"
                                name={user.display_name}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {user.display_name}
                              </p>
                              <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                                {localUserEmails[user.id] || user.email}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 flex-shrink-0 mx-3">
                            {!hasOriginalEmail && (
                            <button
                              type="button"
                              onClick={() => {
                                const current = localUserEmails[user.id] || user.email || '';
                                setLocalUserEmails(prev => ({ ...prev, [user.id]: current }));
                                setEditingUsers(prev => ({ ...prev, [user.id]: true }));
                              }}
                              className="text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                              title="Edit email"
                            >
                              <HiPencil className="w-4 h-4" />
                            </button>
                            )}
                            <button
                              onClick={() => handleRemoveUser(user.id)}
                              className="w text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                              title="Remove user"
                            >
                              <HiX className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 border-t rounded-t-md border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <div className="flex justify-between items-center">
          <button
            onClick={handleBack}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600 transition-colors"
          >
            <HiChevronLeft className="w-4 h-4 mr-2" />
            Back
          </button>

          <button
            onClick={handleProceedToImport}
            disabled={!canProceed || selectedUsers.length === 0}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-blue-600 dark:hover:bg-blue-700 transition-colors"
          >
            Import ({selectedUsers.length})
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailCollectionStep;
